
import { Construction } from "lucide-react";
import { SistemaConfig, tiposEstrutura, nomesEstrutura, EstruturaConfig } from "@/types";

export const getDefaultEstruturas = (): EstruturaConfig[] => {
  return tiposEstrutura.map(tipo => ({
    tipo,
    valorPorKwp: tipo === "sem_estrutura" ? 0 : 400,
    descricao: `Estrutura ${nomesEstrutura[tipo] || tipo}`
  }));
};

export const getDefaultDescricao = (tipo: string): string => {
  switch (tipo) {
    case "tradicional": return "Sistema fotovoltaico com inversor string tradicional";
    case "mlpe": return "Sistema com otimizadores ou microinversores (Module Level Power Electronics)";
    case "hibrido": return "Sistema fotovoltaico com banco de baterias para backup";
    case "offgrid": return "Sistema fotovoltaico autônomo sem conexão à rede";
    default: return "";
  }
};

export const getDefaultValorPorKwp = (tipo: string): number => {
  switch (tipo) {
    case "tradicional": return 3800;
    case "mlpe": return 4500;
    case "hibrido": return 5200;
    case "offgrid": return 6500;
    default: return 4000;
  }
};

export const getDefaultEficiencia = (tipo: string): number => {
  switch (tipo) {
    case "tradicional": return 0.85;
    case "mlpe": return 0.88;
    case "hibrido": return 0.83;
    case "offgrid": return 0.8;
    default: return 0.85;
  }
};

export const getDefaultPerdas = (tipo: string): number => {
  switch (tipo) {
    case "tradicional": return 0.15;
    case "mlpe": return 0.12;
    case "hibrido": return 0.17;
    case "offgrid": return 0.2;
    default: return 0.15;
  }
};

export const getDefaultFatorSimultaneidade = (tipo: string): number => {
  switch (tipo) {
    case "tradicional": return 0.9;
    case "mlpe": return 0.92;
    case "hibrido": return 0.95;
    case "offgrid": return 0.98;
    default: return 0.9;
  }
};

export const getDefaultFatorCapacidade = (tipo: string): number => {
  switch (tipo) {
    case "tradicional": return 0.18;
    case "mlpe": return 0.2;
    case "hibrido": return 0.19;
    case "offgrid": return 0.21;
    default: return 0.18;
  }
};

export const getSistemaSalvo = (tipo: string): any => {
  const saved = localStorage.getItem(`sistema_${tipo}`);
  if (saved) {
    const parsed = JSON.parse(saved);
    if (!parsed.estruturas || !Array.isArray(parsed.estruturas)) {
      parsed.estruturas = getDefaultEstruturas();
    } else {
      const existingTipos = parsed.estruturas.map((e: EstruturaConfig) => e.tipo);
      tiposEstrutura.forEach(tipo => {
        if (!existingTipos.includes(tipo)) {
          parsed.estruturas.push({
            tipo,
            valorPorKwp: tipo === "sem_estrutura" ? 0 : 400,
            descricao: `Estrutura ${nomesEstrutura[tipo] || tipo}`
          });
        }
      });
    }
    return parsed;
  }

  return {
    nome: `Sistema ${tipo.charAt(0).toUpperCase() + tipo.slice(1)}`,
    descricao: getDefaultDescricao(tipo),
    valorPorKwp: getDefaultValorPorKwp(tipo),
    eficiencia: getDefaultEficiencia(tipo),
    perdas: getDefaultPerdas(tipo),
    fatorSimultaneidade: getDefaultFatorSimultaneidade(tipo),
    fatorCapacidade: getDefaultFatorCapacidade(tipo),
    estruturas: getDefaultEstruturas(),
    marcaPadraoPainel: "",
    modeloPadraoPainel: "",
    fotoPadraoPainel: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?auto=format&fit=crop&w=600&q=80",
    marcaPadraoInversor: "",
    modeloPadraoInversor: "",
    fotoPadraoInversor: "https://images.unsplash.com/photo-1487887235947-a955ef187fcc?auto=format&fit=crop&w=600&q=80"
  };
};

export const getImageHandlers = (tipoSistema: string, 
  setPainelImageTradicional: (file: File | null) => void,
  setInversorImageTradicional: (file: File | null) => void,
  setPainelImageMLPE: (file: File | null) => void,
  setInversorImageMLPE: (file: File | null) => void,
  setPainelImageHibrido: (file: File | null) => void,
  setInversorImageHibrido: (file: File | null) => void,
  setPainelImageOffGrid: (file: File | null) => void,
  setInversorImageOffGrid: (file: File | null) => void,
  painelImagePreviewTradicional: string,
  inversorImagePreviewTradicional: string,
  painelImagePreviewMLPE: string,
  inversorImagePreviewMLPE: string,
  painelImagePreviewHibrido: string,
  inversorImagePreviewHibrido: string,
  painelImagePreviewOffGrid: string,
  inversorImagePreviewOffGrid: string
) => {
  switch (tipoSistema) {
    case "tradicional":
      return {
        setPainelImage: setPainelImageTradicional,
        setInversorImage: setInversorImageTradicional,
        painelImagePreview: painelImagePreviewTradicional,
        inversorImagePreview: inversorImagePreviewTradicional,
        idPainel: 'painel-image-tradicional',
        idInversor: 'inversor-image-tradicional'
      };
    case "mlpe":
      return {
        setPainelImage: setPainelImageMLPE,
        setInversorImage: setInversorImageMLPE,
        painelImagePreview: painelImagePreviewMLPE,
        inversorImagePreview: inversorImagePreviewMLPE,
        idPainel: 'painel-image-mlpe',
        idInversor: 'inversor-image-mlpe'
      };
    case "hibrido":
      return {
        setPainelImage: setPainelImageHibrido,
        setInversorImage: setInversorImageHibrido,
        painelImagePreview: painelImagePreviewHibrido,
        inversorImagePreview: inversorImagePreviewHibrido,
        idPainel: 'painel-image-hibrido',
        idInversor: 'inversor-image-hibrido'
      };
    case "offgrid":
      return {
        setPainelImage: setPainelImageOffGrid,
        setInversorImage: setInversorImageOffGrid,
        painelImagePreview: painelImagePreviewOffGrid,
        inversorImagePreview: inversorImagePreviewOffGrid,
        idPainel: 'painel-image-offgrid',
        idInversor: 'inversor-image-offgrid'
      };
    default:
      return {
        setPainelImage: setPainelImageTradicional,
        setInversorImage: setInversorImageTradicional,
        painelImagePreview: painelImagePreviewTradicional,
        inversorImagePreview: inversorImagePreviewTradicional,
        idPainel: 'painel-image-tradicional',
        idInversor: 'inversor-image-tradicional'
      };
  }
};
